import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { fetchDealerReceiptImageUrl, useAddDealerReceipt, useDealerLedger, useDealerReceipts } from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { DealerProfitReceipt } from '../../types';
import { apiDate, date, money } from '../../utils/format';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import PendingDocumentPicker from '../investor/PendingDocumentPicker';
import TransactionImageLink from '../investor/TransactionImageLink';

/** Mirrors the investor Profit tab: accrued/received/outstanding cards plus the payment history. */
export default function DealerProfitTab({ dealerId }: { dealerId: number }) {
  const { hasRole } = useAuth();
  const canWrite = hasRole('Admin');
  const { data: ledger, isLoading } = useDealerLedger(dealerId);
  const { data: receipts } = useDealerReceipts(dealerId);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const addReceipt = useAddDealerReceipt(dealerId);

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await addReceipt.mutateAsync({
        amount: values.amount as number,
        receiptDate: apiDate(values.receiptDate as dayjs.Dayjs)!,
        paymentMethod: values.paymentMethod as string | undefined,
        notes: values.notes as string | undefined,
        transactionImage,
      });
      message.success('Profit payment recorded.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.setFieldsValue({ receiptDate: dayjs() });
              setOpen(true);
            }}
          >
            Record payment
          </Button>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={() =>
            downloadFile(`/reports/dealer-profit-statement/${dealerId}`, `profit_statement_${dealerId}.pdf`).catch(
              (err) => message.error(errorMessage(err)),
            )
          }
        >
          Download profit statement (PDF)
        </Button>
      </Space>

      {isLoading || !ledger ? (
        <Skeleton active />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card>
              <Statistic title="Accrued profit" value={money(ledger.totalAccruedProfit)} prefix="PKR" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic title="Paid so far" value={money(ledger.totalReceivedProfit)} prefix="PKR" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="Outstanding"
                value={money(ledger.outstandingProfit)}
                prefix="PKR"
                valueStyle={{ color: ledger.outstandingProfit > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card title="Payment history" style={{ marginTop: 16 }} size="small">
        <Table<DealerProfitReceipt>
          rowKey="id"
          size="small"
          dataSource={receipts ?? []}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Date', dataIndex: 'receiptDate', render: date },
            { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
            { title: 'Method', dataIndex: 'paymentMethod', render: (v: string | null) => v ?? '—' },
            { title: 'Notes', dataIndex: 'notes', render: (v: string | null) => v ?? '—' },
            {
              title: 'Transaction proof',
              render: (_, row) => (
                <TransactionImageLink
                  exists={row.hasTransactionImage}
                  title={`Transaction proof — payment #${row.id}`}
                  fetchUrl={() => fetchDealerReceiptImageUrl(dealerId, row.id)}
                />
              ),
            },
            {
              title: 'Receipt',
              render: (_, row) => (
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    downloadFile(
                      `/receipts/dealer-profit-payment/${row.id}`,
                      `profit_payment_receipt_${row.id}.pdf`,
                    ).catch((err) => message.error(errorMessage(err)))
                  }
                >
                  PDF
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Record profit payment"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addReceipt.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
          <Form.Item
            name="amount"
            label="Amount (PKR)"
            rules={[{ required: true }, { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' }]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="receiptDate" label="Payment date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="paymentMethod" label="Payment method">
            <Select options={paymentMethodOptions} allowClear />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="profit-payment-image"
            label="Transaction proof (optional)"
            hint="Receipt or transfer screenshot proving the dealer paid the profit · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
